import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  TrendingUp,
  Zap,
  FileText,
  Loader2,
  Activity,
  PieChart,
  TrendingDown,
  LineChart,
} from "lucide-react";
import { categoryIcons } from "../lib/constants";
import { useApi } from "../hooks/useApi";
import { DashboardLayout } from "../components/DashboardLayout";

export const AnalyticsPage = () => {
  const api = useApi();
  const [valueTrends, setValueTrends] = useState([]);
  const [marketInsights, setMarketInsights] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [trendsRes, insightsRes, categoriesRes] = await Promise.all([
        api.get("/analytics/value-trends?days=30"),
        api.get("/analytics/market-insights"),
        api.get("/dashboard/categories"),
      ]);
      setValueTrends(trendsRes.data.history || []);
      setMarketInsights(insightsRes.data);
      setCategoryData(categoriesRes.data);
    } catch (e) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getTrendIcon = (trend) => {
    if (trend === "rising") return <TrendingUp className="w-4 h-4 text-green-400" />;
    if (trend === "declining") return <TrendingDown className="w-4 h-4 text-red-400" />;
    return <Activity className="w-4 h-4 text-gray-400" />;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto" data-testid="analytics-page">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">Analytics & Insights</h1>
          <p className="text-gray-400 mt-1">Track your portfolio value and market trends</p>
        </div>

        {/* Value Trend Chart */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-white">Portfolio Value Trend</h2>
            <span className="text-sm text-gray-400">Last 30 days</span>
          </div>
          {valueTrends.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <LineChart className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Add data and get valuations to see trends</p>
              </div>
            </div>
          ) : (
            <div className="h-48 flex items-end justify-around gap-2">
              {valueTrends.slice(-14).map((point) => {
                const maxValue = Math.max(...valueTrends.map(p => p.total_value)) || 1;
                const height = (point.total_value / maxValue) * 100;
                return (
                  <div key={point.recorded_at} className="flex-1 flex flex-col items-center gap-1">
                    <div
                      className="w-full bg-gradient-to-t from-purple-600 to-cyan-400 rounded-t-lg min-h-[4px] transition-all"
                      style={{ height: `${Math.max(height, 4)}%` }}
                    />
                    <span className="text-xs text-gray-500">
                      {new Date(point.recorded_at).toLocaleDateString('en-US', { day: 'numeric' })}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          {valueTrends.length > 0 && (
            <div className="mt-4 pt-4 border-t border-purple-500/20 flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Current Value</p>
                <p className="text-2xl font-bold text-white">
                  ${valueTrends[valueTrends.length - 1]?.total_value?.toFixed(2) || '0.00'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-400">Data Points</p>
                <p className="text-lg font-semibold text-white">
                  {valueTrends[valueTrends.length - 1]?.item_count || 0} items
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Market Insights Grid */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4">Market Insights & Forecasts</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {marketInsights.map((insight) => {
              const Icon = categoryIcons[insight.category] || FileText;
              return (
                <div key={insight.category} className="bg-white/5 rounded-xl p-4 border border-purple-500/10">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-purple-400" />
                      <span className="capitalize text-white font-medium">{insight.category}</span>
                    </div>
                    {getTrendIcon(insight.trend)}
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Change</span>
                      <span className={insight.change_percent > 0 ? 'text-green-400' : insight.change_percent < 0 ? 'text-red-400' : 'text-gray-400'}>
                        {insight.change_percent > 0 ? '+' : ''}{insight.change_percent}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Avg Price</span>
                      <span className="text-white">${insight.avg_price.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Buyers</span>
                      <span className="text-white">{insight.top_buyers}</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-3 pt-3 border-t border-purple-500/10">
                    {insight.forecast}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Category Performance */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20">
          <h2 className="text-lg font-semibold text-white mb-4">Your Category Performance</h2>
          {categoryData.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <PieChart className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Add data to see category breakdown</p>
            </div>
          ) : (
            <div className="space-y-3">
              {categoryData.map((cat) => {
                const Icon = categoryIcons[cat.category] || FileText;
                const maxValue = Math.max(...categoryData.map(c => c.total_value)) || 1;
                const percentage = (cat.total_value / maxValue) * 100;
                return (
                  <div key={cat.category} className="p-4 bg-white/5 rounded-xl border border-purple-500/10">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5 text-purple-400" />
                        <span className="capitalize text-white font-medium">{cat.category}</span>
                        {cat.trending && <Zap className="w-4 h-4 text-yellow-400" />}
                      </div>
                      <div className="text-right">
                        <p className="text-white font-bold">${cat.total_value.toFixed(2)}</p>
                        <p className="text-xs text-gray-500">{cat.count} items</p>
                      </div>
                    </div>
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-purple-600 to-cyan-400 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                      <span>Demand: {cat.demand_level}</span>
                      <span>{cat.valuated}/{cat.count} valuated</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

// Privacy Page
